/**
 * @module modules/picker-field-sk
 * @description <h2><code>picker-field-sk</code></h2>
 *
 * A stylized text field that let's the user pick a value from a pre-selected
 * list of valid options. Specifically, designed to be used by the new Test
 * Picker component, but may be reused for other purposes.
 *
 * @evt value-changed - This event gets triggered whenever a text field value
 * is selected. Clearing the text field counts as selecting a value. The
 * value will be available in event.detail.value.
 *
 * @attr {string} label - The label to be used on top of the text field and as
 * a placeholder in the text field.
 *
 * @attr {string[]} options - A valid selection of options that'll be displayed
 * as options to the user in the dropdown menu.
 *
 * @example
 * <picker-field-sk
 *  .label="benchmark"
 *  .options=${["Speedometer2", "Jetstream2"]}
 * >
 * </picker-field-sk>
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "PickerFieldSk", {
    enumerable: true,
    get: function() {
        return PickerFieldSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _checkboxsk = require("../../../elements-sk/modules/checkbox-sk/checkbox-sk");
require("@vaadin/multi-select-combo-box/theme/lumo/vaadin-multi-select-combo-box.js");
const _multiselectcombobox = require("@vaadin/multi-select-combo-box");
const _testpicker = require("../common/test-picker");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let PickerFieldSk = class PickerFieldSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('options')) {
            this._primaryOptions = this.options.filter((option)=>!option.includes('.'));
            // Calculate overlay width
            let maxLength = 0;
            this.options.forEach((option)=>{
                if (option.length > maxLength) {
                    maxLength = option.length;
                }
            });
            this._overlayWidth = `${maxLength + 5}ch`;
        }
        // Propagate disabled state to underlying logic if needed.
        if (changedProperties.has('splitDisabled') && this.splitDisabled) {
            if (this.split) {
                this.split = false;
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this._setupObserver();
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this._setupObserver();
    }
    disconnectedCallback() {
        var _this__observer;
        super.disconnectedCallback();
        (_this__observer = this._observer) === null || _this__observer === void 0 ? void 0 : _this__observer.disconnect();
        this._observer = undefined;
    }
    _setupObserver() {
        if (this._comboBox && !this._observer) {
            this._observer = new MutationObserver(()=>this._updateChipsTooltip());
            this._observer.observe(this._comboBox, {
                childList: true,
                subtree: true
            });
            this._updateChipsTooltip();
        }
    }
    _updateChipsTooltip() {
        if (!this._comboBox) return;
        const chips = this._comboBox.querySelectorAll('vaadin-multi-select-combo-box-chip');
        chips.forEach((chip)=>{
            if (chip.classList.contains('has-help-icon')) {
                const helpText = `Selects traces without ${this.label} traceparam.`;
                chip.setAttribute('title', helpText);
            }
        });
    }
    render() {
        const labelId = `label-${this.label.replace(/\s+/g, '-')}`;
        return (0, _lit.html)`
      <div id="picker-field-${this.label}">
        <div class="header-row">
          <label id="${labelId}">${this.label}</label>
          <div id="split-by-container">
            <checkbox-sk
              title="Split the chart by attribute."
              name=${this.label}
              id="split-by"
              label="Split"
              @change=${this.splitOnValue}
              .checked=${this.split}
              ?disabled=${this.disabled || this.splitDisabled}
              ?hidden=${!this.showSplit}>
            </checkbox-sk>
            <checkbox-sk
              title="Select all values without periods in the name."
              name=${this.label}
              id="select-primary"
              label="Primary"
              @change=${this.selectPrimary}
              .checked=${this._arePrimarySelected}
              ?disabled=${this.disabled}
              ?hidden=${!this.showPrimary}>
            </checkbox-sk>
            <checkbox-sk
              title="Select All"
              name=${this.label}
              id="select-all"
              label="All"
              @change=${this.selectAll}
              .checked=${this._isAllSelected}
              ?disabled=${this.disabled}
              ?hidden=${!this.showSelectAll}>
            </checkbox-sk>
          </div>
        </div>
        <vaadin-multi-select-combo-box
          auto-expand-vertically
          aria-labelledby="${labelId}"
          .items=${this.options}
          .selectedItems=${this.selectedItems}
          .itemClassNameGenerator=${this._itemClassNameGenerator}
          @selected-items-changed=${this.onValueChanged}
          ?readonly=${this.disabled}
          selected-items-on-top
          style="width: ${this._overlayWidth}; --vaadin-multi-select-combo-box-overlay-width: ${this._overlayWidth}">
        </vaadin-multi-select-combo-box>
      </div>
    `;
    }
    /**
   * Handles the 'selected-items-changed' event from the
   * vaadin-multi-select-combo-box.
   * Dispatches a custom 'value-changed' event with the new selected items.
   * @param e The event object.
   */ onValueChanged(e) {
        const selectedItems = e.detail.value;
        this.dispatchEvent(new CustomEvent('value-changed', {
            detail: {
                value: selectedItems,
                checkboxSelected: this._checkboxSelected
            },
            bubbles: true,
            composed: true
        }));
        this._checkboxSelected = false;
        // Update internal state AFTER dispatching so parent components
        // can read the old value synchronously during the event stack.
        this.selectedItems = selectedItems;
    }
    /**
   * Handles the change event for the "Split By" checkbox.
   * It updates the \`_splitBy\` property and dispatches a custom event
   * to notify that the split option has changed.
   *
   * @param e - The event triggered by the checkbox change.
   */ splitOnValue(e) {
        this.split = e.currentTarget.checked;
        this.dispatchEvent(new CustomEvent('split-by-changed', {
            detail: {
                param: this.label,
                split: this.split
            },
            bubbles: true,
            composed: true
        }));
    }
    /**
   * Selects all options if the "Select All" checkbox is checked.
   * If it is unchecked, it clears the selection.
   *
   * @param e - The event triggered by the checkbox change.
   */ selectAll(e) {
        if (this._allSelected) {
            this._checkboxSelected = true;
            if (e.currentTarget.checked) {
                // Create a shallow copy to avoid direct mutation issues.
                this.selectedItems = this.options.slice();
            } else {
                // Leave the first item selected.
                this.selectedItems = this.options.slice(0, 1);
            }
        }
    }
    dispatchValueChanged() {
        this.dispatchEvent(new CustomEvent('value-changed', {
            detail: {
                value: this.selectedItems,
                checkboxSelected: this._checkboxSelected
            },
            bubbles: true,
            composed: true
        }));
    }
    /**
   * Selects or unselects primary options based on the checkbox state.
   * If checked, it adds all primary options to the current selection.
   * If unchecked, it removes all primary options from the current selection.
   */ selectPrimary(e) {
        if (this._primarySelected) {
            this._checkboxSelected = true;
            if (e.currentTarget.checked) {
                // If all is selected, deselect all and select primary.
                if (this._isAllSelected) {
                    this.selectedItems = [];
                }
                // Add all primary options to the current selection, ensuring no duplicates.
                const newSelection = [
                    ...new Set([
                        ...this.selectedItems,
                        ...this.primaryOptions
                    ])
                ];
                this.selectedItems = newSelection;
            } else {
                // Leave the first item selected.
                this.selectedItems = this.options.slice(0, 1);
            }
        }
    }
    /**
   * Sets focus on the internal vaadin-multi-select-combo-box element.
   */ focus() {
        var _this__comboBox;
        (_this__comboBox = this._comboBox) === null || _this__comboBox === void 0 ? void 0 : _this__comboBox.focus();
    }
    /**
   * Opens the overlay (dropdown) of the internal
   * vaadin-multi-select-combo-box element.
   */ openOverlay() {
        var _this__comboBox;
        (_this__comboBox = this._comboBox) === null || _this__comboBox === void 0 ? void 0 : _this__comboBox.click();
    }
    /**
   * Returns true if all options are currently selected.
   * @returns True if all options are selected, false otherwise.
   */ get _isAllSelected() {
        if (this.options.length === 0) {
            return false;
        }
        return this.selectedItems.length === this.options.length;
    }
    /**
   * Returns true if all primary options are currently selected.
   * @returns True if all primary options are selected, false otherwise.
   */ get _arePrimarySelected() {
        if (this._primaryOptions.length === 0) {
            return false;
        }
        const show = this._primaryOptions.every((p)=>this.selectedItems.includes(p));
        // Check if lengths match (meaning no extra non-primary items selected? or just all primaries selected?)
        // Original logic:
        // return this._primaryOptions.length === this.selectedItems.length && show;
        // This allows exact match only.
        return this._primaryOptions.length === this.selectedItems.length && show;
    }
    /**

   * Returns true if the "Select All" checkbox should be shown.
   */ get showSelectAll() {
        return this.options.length > 2 && this.index > 0;
    }
    /**
   * Returns true if the "Split" checkbox should be shown.
   */ get showSplit() {
        return this.selectedItems.length > 1 && this.index > 0;
    }
    /**
   * Returns true if the "Primary" checkbox should be shown.
   */ get showPrimary() {
        return this._primaryOptions.length > 0 && this._primaryOptions.length !== this.options.length && this.index > 0;
    }
    get primaryOptions() {
        return this._primaryOptions;
    }
    /**
   * Creates an instance of PickerFieldSk.
   * @param label The label for the picker field.
   * @param disableSplit (Optional) Whether to permanently disable/hide the split functionality.
   */ constructor(label = '', disableSplit = false){
        super();
        this.label = '';
        this.helperText = '';
        this.options = [];
        this.index = 0;
        this.split = false;
        this.selectedItems = [];
        // Used to control split availability from constructor/parent
        this.splitDisabled = false;
        this.disabled = false;
        this._checkboxSelected = false;
        this._primaryOptions = [];
        this._overlayWidth = '5ch';
        this._itemClassNameGenerator = (item)=>{
            if (item === _testpicker.DEFAULT_OPTION_LABEL) {
                return 'has-help-icon';
            }
            return '';
        };
        this.label = label;
        this.splitDisabled = disableSplit;
    }
};
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PickerFieldSk.prototype, "label", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String,
        attribute: 'helper-text'
    }),
    _ts_metadata("design:type", String)
], PickerFieldSk.prototype, "helperText", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Array,
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], PickerFieldSk.prototype, "options", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Number
    }),
    _ts_metadata("design:type", Number)
], PickerFieldSk.prototype, "index", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean
    }),
    _ts_metadata("design:type", Boolean)
], PickerFieldSk.prototype, "split", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Array,
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], PickerFieldSk.prototype, "selectedItems", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        attribute: 'split-disabled'
    }),
    _ts_metadata("design:type", Boolean)
], PickerFieldSk.prototype, "splitDisabled", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], PickerFieldSk.prototype, "disabled", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], PickerFieldSk.prototype, "_checkboxSelected", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Array)
], PickerFieldSk.prototype, "_primaryOptions", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], PickerFieldSk.prototype, "_overlayWidth", void 0);
_ts_decorate([
    (0, _decorators.query)('vaadin-multi-select-combo-box'),
    _ts_metadata("design:type", typeof _multiselectcombobox.MultiSelectComboBox === "undefined" ? Object : _multiselectcombobox.MultiSelectComboBox)
], PickerFieldSk.prototype, "_comboBox", void 0);
_ts_decorate([
    (0, _decorators.query)('checkbox-sk#split-by'),
    _ts_metadata("design:type", typeof _checkboxsk.CheckOrRadio === "undefined" ? Object : _checkboxsk.CheckOrRadio)
], PickerFieldSk.prototype, "_splitBox", void 0);
_ts_decorate([
    (0, _decorators.query)('checkbox-sk#select-all'),
    _ts_metadata("design:type", typeof _checkboxsk.CheckOrRadio === "undefined" ? Object : _checkboxsk.CheckOrRadio)
], PickerFieldSk.prototype, "_allSelected", void 0);
_ts_decorate([
    (0, _decorators.query)('checkbox-sk#select-primary'),
    _ts_metadata("design:type", typeof _checkboxsk.CheckOrRadio === "undefined" ? Object : _checkboxsk.CheckOrRadio)
], PickerFieldSk.prototype, "_primarySelected", void 0);
PickerFieldSk = _ts_decorate([
    (0, _decorators.customElement)('picker-field-sk'),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String,
        Boolean
    ])
], PickerFieldSk);
